import React from "react"
import { graphql } from "gatsby"
import Layout from "components/Layout"
import SeoHead from "components/SEO"
import SliceZone from "components/SliceZone"
import { linkResolver } from "utils/linkResolver"

import { withPrismicPreview } from "gatsby-plugin-prismic-previews"

function Page({ data, ...props }) {
  if (!data) return null
  const { site, prismicSiteSettings, prismicPage: page } = data
  const { data: site_settings } = prismicSiteSettings
  const { body } = page.data
  const { siteUrl } = site.siteMetadata
  const uri = linkResolver(page)

  const { nodes: blogPosts } = data.allPrismicBlogPost
  const { numPages } = props.pageContext

  // console.log(props, data)

  return (
    <Layout site_settings={site_settings} lang={page.lang}>
      <SliceZone
        slices={body}
        pageInfo={{ uri, host: siteUrl, title: page.data.title.text }}
        firstBlogPosts={blogPosts}
        numPages={numPages}
        {...props}
      />
    </Layout>
  )
}

export { Page }
export default withPrismicPreview(Page)

export const Head = ({ data }) => {
  const { site, prismicSiteSettings, prismicPage: page } = data
  return (
    <SeoHead
      siteMetadata={site.siteMetadata}
      siteSettings={prismicSiteSettings}
      lang={page.lang}
      slug={linkResolver(page)}
      title={page.data.meta_title}
      schemaTitle={page.data.title.text}
      description={page.data.meta_description}
      images={page.data.meta_images}
      pageType={page.uid === "viden" ? "CollectionPage" : "WebPage"}
      dates={{
        published: page.first_publication_date,
        modified: page.last_publication_date,
      }}
    />
  )
}

export const query = graphql`
  # query PrismicPage($lang: String, $uid: String!, $isProduction: Boolean!) {
  # $limit is used for the first blog page only to limit the number of posts
  query PrismicPage($lang: String, $uid: String!, $limit: Int!) {
    site {
      ...SiteSeoMetadata
    }
    prismicSiteSettings {
      _previewable
      ...SiteNavigation
      ...FooterInformation
      ...FooterNavigation
      ...SocialMediaLinks
      ...UtilityNavigation
    }
    # for the first blog page only
    allPrismicBlogPost(
      sort: { fields: data___publication_date, order: DESC }
      limit: $limit
      skip: 0
      filter: { data: { public_publish: { eq: true } } }
    ) {
      nodes {
        ...BlogPostFragment
        ...BlogPostBodyFragment
      }
    }
    # for any page (including first blog page)
    prismicPage(lang: { eq: $lang }, uid: { eq: $uid }) {
      _previewable
      type
      lang
      uid
      first_publication_date
      last_publication_date
      ...FeaturedBlogposts
      data {
        title {
          richText
          text
        }
        meta_title
        meta_description
        meta_images {
          image {
            dimensions {
              width
              height
            }
            alt
            copyright
            url
            localFile {
              childImageSharp {
                id
                main: fixed(width: 1200) {
                  ...GatsbyImageSharpFixed_withWebp
                }
              }
            }
          }
        }
        show_in_sitemap
        body {
          ... on PrismicPageDataBodyParagraph {
            slice_type
            primary {
              paragraph_title {
                richText
                raw
              }
              paragraph_content {
                richText
                raw
              }
              center_paragraph
              bullet_color
              bullet_background_color
              bullet_icon_alt {
                url
              }
            }
          }
          ... on PrismicPageDataBodyBlogposts {
            ...PageRelatedBlogposts
          }
          ... on PrismicPageDataBodyTestimonial {
            ...TestimonialPageFragment
          }
          ... on PrismicPageDataBodyParagraphImage {
            slice_type
            primary {
              theme
              image_position
              content {
                richText
                raw
              }
              bullet_color
              bullet_background_color
              bullet_icon_alt {
                url
              }
              paragraph_image {
                dimensions {
                  width
                  height
                }
                alt
                copyright
                url
                localFile {
                  childImageSharp {
                    id
                    desktop: gatsbyImageData(
                      layout: FIXED
                      width: 960
                      height: 960
                    )
                    mobile: gatsbyImageData(
                      layout: FIXED
                      width: 400
                      height: 400
                    )
                    wide: gatsbyImageData(
                      layout: FIXED
                      width: 1920
                      height: 1200
                    )
                    square: gatsbyImageData(
                      layout: FIXED
                      width: 960
                      height: 960
                    )
                  }
                }
              }
              paragraph_image_style
            }
            items {
              cta_link {
                link_type
                document {
                  ... on PrismicLeadModule {
                    id
                    type
                    uid
                    lang
                  }
                  ... on PrismicPage {
                    uid
                    type
                    lang
                    id
                  }
                }
                url
              }
              cta_link_text
            }
          }
          ... on PrismicPageDataBodyIllustratedUsp {
            slice_type
            primary {
              illustration_position
              illustration {
                dimensions {
                  width
                  height
                }
                alt
                copyright
                url
                localFile {
                  childImageSharp {
                    id
                    desktop: gatsbyImageData(layout: FIXED, width: 600)
                    mobile: gatsbyImageData(layout: FIXED, width: 400)
                  }
                }
              }
              usp {
                document {
                  ... on PrismicUsp {
                    ...USPFragment
                  }
                }
              }
            }
            items {
              cta_link {
                link_type
                document {
                  ... on PrismicLeadModule {
                    id
                    type
                    uid
                    lang
                  }
                  ... on PrismicPage {
                    id
                    uid
                    type
                    lang
                  }
                }
              }
              cta_link_text
            }
          }
          ... on PrismicPageDataBodyUspRepeater {
            slice_type
            primary {
              theme
              paragraph_title {
                richText
                raw
              }
              paragraph_content {
                richText
                raw
              }
              paragraph_content_footer {
                richText
                raw
              }
              columns
              use_cards
            }
            items {
              use_counter
              unit
              unit_money
              count_to
              usp_title {
                richText
                raw
              }
              content {
                richText
                raw
              }
              content_footer {
                richText
                raw
              }
              usp_icon {
                url
              }
              center_content
            }
          }
          ... on PrismicPageDataBodyHeroSection {
            slice_type
            primary {
              theme
              bullets {
                richText
                raw
              }
              bullet_color
              bullet_background_color
              bullet_icon_alt {
                url
              }
              content {
                richText
                raw
              }
              headline {
                richText
                raw
              }
              pre_headline
              image {
                dimensions {
                  width
                  height
                }
                alt
                copyright
                url
                localFile {
                  childImageSharp {
                    id
                    desktop: gatsbyImageData(layout: FIXED, width: 960)
                    mobile: gatsbyImageData(layout: FIXED, width: 480)
                    wide: gatsbyImageData(
                      layout: FIXED
                      width: 1920
                      height: 1200
                    )
                    square: gatsbyImageData(
                      layout: FIXED
                      width: 960
                      height: 960
                    )
                  }
                }
              }
              background_image {
                dimensions {
                  width
                  height
                }
                alt
                copyright
                url
                localFile {
                  childImageSharp {
                    id
                    desktop: gatsbyImageData(layout: FIXED, width: 960)
                    mobile: gatsbyImageData(layout: FIXED, width: 480)
                    wide: gatsbyImageData(
                      layout: FIXED
                      width: 1920
                      height: 1200
                    )
                    square: gatsbyImageData(
                      layout: FIXED
                      width: 960
                      height: 960
                    )
                  }
                }
              }
              background_video {
                url
              }
            }
            items {
              cta_link {
                link_type
                document {
                  ... on PrismicLeadModule {
                    id
                    type
                    uid
                    lang
                  }
                  ... on PrismicPage {
                    id
                    uid
                    type
                    lang
                  }
                }
                url
              }
              cta_link_text
              cta_link_external {
                target
                url
                link_type
              }
            }
          }
          ... on PrismicPageDataBodyLeadCta {
            slice_type
            primary {
              use_theme
              theme
              layout
              lead_title {
                richText
                raw
              }
              lead_content {
                richText
                raw
              }
              lead_module {
                document {
                  ... on PrismicLeadModule {
                    ...LeadModule
                  }
                }
              }
              person {
                document {
                  ... on PrismicPerson {
                    ...PersonFragment
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyFeatureRepeater {
            slice_type
            primary {
              theme
              features_content {
                richText
                raw
              }
              features_title {
                richText
                raw
              }
            }
            items {
              feature_description {
                richText
                raw
              }
              feature_icon {
                url
              }
            }
          }
          ... on PrismicPageDataBodyFeatureComparison {
            slice_type
            primary {
              product_features_title {
                richText
                raw
              }
              feature_column_title
              feature_not_in_product {
                richText
                raw
              }
            }
            items {
              product_feature {
                document {
                  ... on PrismicProductFeature {
                    id
                    data {
                      title
                      description {
                        richText
                        raw
                      }
                      products {
                        product {
                          document {
                            ... on PrismicProduct {
                              ...ProductFragment
                            }
                          }
                        }
                        product_feature_description {
                          richText
                          raw
                        }
                        supported
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyPersonCta {
            slice_type
            primary {
              person {
                document {
                  ... on PrismicPerson {
                    ...PersonFragment
                  }
                }
              }
              person_cta_content {
                richText
                raw
              }
              person_cta_title {
                richText
                raw
              }
            }
            items {
              person_cta_action
              person_cta_link_text
            }
          }
          ... on PrismicPageDataBodyPersonRepeater {
            slice_type
            primary {
              persons_content {
                richText
                raw
              }
              persons_title {
                richText
                raw
              }
            }
            items {
              person {
                document {
                  ... on PrismicPerson {
                    ...PersonFragment
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyParagraph {
            slice_type
            primary {
              theme
              title_position
              paragraph_title {
                richText
                raw
              }
              paragraph_content {
                richText
                raw
              }
            }
            items {
              cta_link {
                link_type
                document {
                  ... on PrismicLeadModule {
                    id
                    type
                    uid
                    lang
                  }
                  ... on PrismicPage {
                    uid
                    type
                    lang
                    id
                  }
                }
                url
              }
              cta_link_text
            }
          }
          ... on PrismicPageDataBodyClientQuotations {
            id
            slice_type
            primary {
              headline: heading {
                richText
                raw
              }
            }
            items {
              client_quotation {
                document {
                  ... on PrismicClientQuotation {
                    ...ClientQuotationFragment
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyImageRepeater {
            slice_type
            primary {
              direction
              images_content {
                richText
                raw
              }
              images_title {
                text
                richText
                raw
              }
              shuffle_text
            }
            items {
              image {
                dimensions {
                  width
                  height
                }
                alt
                copyright
                url
                localFile {
                  childImageSharp {
                    id
                    desktop: gatsbyImageData(
                      layout: FIXED
                      width: 300
                      formats: [WEBP]
                    )
                    mobile: gatsbyImageData(
                      layout: FIXED
                      width: 300
                      formats: [WEBP]
                    )
                  }
                }
              }
              description {
                richText
                raw
                text
              }
            }
          }
          ... on PrismicPageDataBodyCardRepeater {
            slice_type
            primary {
              columns
              card_repeater_title {
                richText
                raw
              }
              card_repeater_content_after {
                richText
                raw
                text
              }
              card_repeater_content {
                richText
                raw
              }
            }
            items {
              content {
                document {
                  ... on PrismicProduct {
                    ...ProductFragment
                  }
                }
              }
              emphasize: emphasize_
              display_style
              cta_text
              background_image {
                alt
                copyright
                url
              }
            }
          }
          ... on PrismicPageDataBodyFaqRepeater {
            slice_type
            primary {
              faq_repeater_title {
                richText
                raw
              }
              faq_repeater_content {
                richText
                raw
              }
            }
            items {
              faq_link {
                document {
                  ... on PrismicFaq {
                    data {
                      question {
                        richText
                        raw
                      }
                      answer {
                        richText
                        raw
                      }
                    }
                    id
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyTitle {
            slice_type
            primary {
              content_title {
                richText
                raw
              }
              content {
                richText
                raw
              }
            }
          }
          ... on PrismicPageDataBodyButton {
            slice_type
            primary {
              type
              button_text
              page_link {
                document {
                  ... on PrismicPage {
                    id
                    type
                    lang
                    uid
                  }
                  ... on PrismicIndex {
                    id
                    type
                    lang
                    uid
                  }
                  ... on PrismicLeadModule {
                    id
                    type
                    uid
                    lang
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyPartnerRepeater {
            slice_type
            primary {
              partners_title {
                richText
                raw
                text
              }
              content {
                richText
                raw
              }
              shuffle_text: shuffle_test
            }
            items {
              partner_name
              partner_link {
                url
                target
                link_type
              }
              partner_logo_image {
                dimensions {
                  width
                  height
                }
                alt
                copyright
                url
                localFile {
                  childImageSharp {
                    desktop: gatsbyImageData(
                      layout: FIXED
                      width: 412
                      formats: [WEBP]
                    )
                    mobile: gatsbyImageData(
                      layout: FIXED
                      width: 270
                      formats: [WEBP]
                    )
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyAddonProducts {
            slice_type
            primary {
              addons_title {
                richText
                raw
              }
              button_text
              legal_footer {
                richText
                raw
                text
              }
              cta_link {
                link_type
                document {
                  ... on PrismicLeadModule {
                    id
                    type
                    uid
                    lang
                  }
                  ... on PrismicPage {
                    id
                    uid
                    type
                    lang
                  }
                }
                url
              }
              cta_link_text
            }
            items {
              image {
                dimensions {
                  width
                  height
                }
                alt
                copyright
                url
                localFile {
                  childImageSharp {
                    fluid {
                      src
                    }
                  }
                }
              }
              icon {
                url
              }
              addon_title {
                richText
                raw
              }
              addon_benefits {
                richText
                raw
              }
              price
              payment_term
              link {
                document {
                  ... on PrismicPage {
                    id
                    type
                    lang
                    uid
                  }
                  ... on PrismicIndex {
                    id
                    type
                    lang
                    uid
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyFeaturedBlogPosts {
            slice_type
            primary {
              featured_tags
              hero_post {
                document {
                  ... on PrismicBlogPost {
                    ...BlogPostFragment
                  }
                }
              }
            }
            items {
              featured_extras {
                document {
                  ... on PrismicBlogPost {
                    ...BlogPostFragment
                    ...BlogPostBodyFragment
                  }
                }
              }
            }
          }
          ... on PrismicPageDataBodyProductPresentation {
            ...ProductPresentationPageFragment
          }
          ... on PrismicPageDataBodyBlogPostFeed {
            slice_type
            primary {
              sidebar_content {
                richText
                raw
              }
            }
          }
        }
      }
    }
  }
`
